import { isAllDay, type AgendaDates } from "@/lib/agendaDates";
export interface PlanEvent extends AgendaDates { id: string; title: string }
export interface DaySlot { start: Date; end: Date }
export function occursDuringDay(event: AgendaDates, day: Date) {
  const start = new Date(day); start.setHours(0, 0, 0, 0);
  const end = new Date(start); end.setDate(end.getDate() + 1);
  const eventStart = new Date(isAllDay(event) ? event.all_day_start + "T00:00:00" : event.start_at);
  const eventEnd = isAllDay(event) ? new Date(event.all_day_end + "T00:00:00") : event.end_at ? new Date(event.end_at) : null;
  return eventStart < end && (eventEnd && eventEnd > eventStart ? eventEnd > start : eventStart >= start);
}
/** Planning only covers loaded VYV events and the displayed 08:00–20:00 window. */
export function buildAgendaDayPlan(events: PlanEvent[], day: Date, now: Date) {
  const windowStart = new Date(day); windowStart.setHours(8, 0, 0, 0);
  const windowEnd = new Date(day); windowEnd.setHours(20, 0, 0, 0);
  const dayEvents = events.filter(event => occursDuringDay(event, day));
  const allDay = dayEvents.filter(isAllDay);
  const timed = dayEvents.filter(event => !isAllDay(event));
  const incomplete = timed.some(event => !event.end_at || !Number.isFinite(Date.parse(event.end_at)) || Date.parse(event.end_at) <= Date.parse(event.start_at));
  const ranges = timed.filter(event => event.end_at && Date.parse(event.end_at) > Date.parse(event.start_at))
    .map(event => ({ event, start: new Date(event.start_at), end: new Date(event.end_at!) })).sort((a, b) => +a.start - +b.start);
  const conflicts: Array<[PlanEvent, PlanEvent]> = [];
  for (let i = 0; i < ranges.length; i++) for (let j = i + 1; j < ranges.length; j++) {
    if (ranges[j].start >= ranges[i].end) break;
    conflicts.push([ranges[i].event, ranges[j].event]);
  }
  const slots: DaySlot[] = [];
  // An all-day commitment or missing end time makes availability uncertain.
  if (!allDay.length && !incomplete && now < windowEnd) {
    let cursor = Math.max(+windowStart, +now);
    if (cursor > +windowStart) {
      const rounded = new Date(cursor); rounded.setSeconds(0, 0);
      rounded.setMinutes(Math.ceil((new Date(cursor).getMinutes() + (cursor % 60000 > 0 ? 1 : 0)) / 15) * 15);
      cursor = +rounded;
    }
    for (const range of ranges) {
      const end = Math.min(+range.start, +windowEnd);
      if (end - cursor >= 30 * 60000) slots.push({ start: new Date(cursor), end: new Date(end) });
      cursor = Math.max(cursor, +range.end);
    }
    if (+windowEnd - cursor >= 30 * 60000) slots.push({ start: new Date(cursor), end: windowEnd });
  }
  return { dayEvents, conflicts, slots, incomplete, allDayCount: allDay.length };
}
